import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import Modal from './Modal';

/**
 * Confirmation dialogs, replacing `window.confirm` and the two-click-delete pattern.
 *
 * `window.confirm` blocks the whole tab, cannot say what will actually be deleted
 * beyond one line of plain text, and looks nothing like the app. The two-click
 * delete was worse: the first click had no visible effect on some surfaces, so it
 * read as a broken button, and the armed state expired silently after 5 seconds.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete invoice?', destructive: true })) { ... }
 */

export interface ConfirmOptions {
  title: string;
  /** Say what will happen, and what cannot be undone. */
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button, for irreversible actions. */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  // Guards against the caller resolving twice (Escape then Cancel, say).
  const settled = useRef(false);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        settled.current = false;
        setPending({ ...options, resolve });
      }),
    []
  );

  const settle = useCallback((value: boolean) => {
    setPending((current) => {
      if (current && !settled.current) {
        settled.current = true;
        current.resolve(value);
      }
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={pending !== null}
        onClose={() => settle(false)}
        size="sm"
        title={
          pending?.destructive ? (
            <>
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              {pending?.title}
            </>
          ) : (
            pending?.title
          )
        }
        footer={
          <>
            <button
              type="button"
              onClick={() => settle(false)}
              className="px-4 py-2 rounded-lg border border-stone-300 dark:border-stone-600 text-sm"
            >
              {pending?.cancelLabel ?? 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => settle(true)}
              className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
                pending?.destructive
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-coral-600 hover:bg-coral-700'
              }`}
            >
              {pending?.confirmLabel ?? 'Confirm'}
            </button>
          </>
        }
      >
        {pending?.message && (
          <div className="text-sm text-stone-600 dark:text-stone-300">{pending.message}</div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
};

/**
 * Returns an async confirm().
 *
 * Without a provider it resolves `false` — refusing rather than silently
 * performing a destructive action nobody confirmed.
 */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  return ctx ?? (async () => false);
};

/** Small spinner for buttons that stay mounted while their action runs. */
export const ButtonSpinner: React.FC = () => <Loader2 className="w-4 h-4 animate-spin" />;

export default ConfirmProvider;
